// Eskom PARCC — fiscal calendar for quarterly + cumulative reporting.
//
// The client (Lindiwe, 10 Sep 2026) reports on the SA financial year (Apr–Mar)
// split into quarters, with a per-quarter high-level slide and a cumulative
// year-to-date detailed section. Nothing in the register carries a transaction
// DATE — `Transaction.period` is a free-text quarter (Q1..Q4) and `year` a
// free-text financial year — so this module is the single place that (a) folds
// the messy stored values onto the canonical vocabulary and (b) turns a real
// calendar date (report as-of, or "today") into an {fy, quarter}.
//
// Pure, no React, clock-free except todayIsoSast(). Unit-tested in
// test/domain/fiscal.test.ts.

/** A financial-year quarter. Q1 = Apr–Jun … Q4 = Jan–Mar (SA fiscal year). */
export type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';

/** The four quarters in fiscal order. */
export const QUARTERS: readonly Quarter[] = ['Q1', 'Q2', 'Q3', 'Q4'] as const;

/**
 * Each quarter's calendar months (1–12) and the client's slide-title label
 * (en-dash, upper-case — matches her deck: "APRIL – JUNE").
 */
export const QUARTER_MONTHS: Record<Quarter, { months: readonly [number, number, number]; label: string }> = {
  Q1: { months: [4, 5, 6], label: 'APRIL – JUNE' },
  Q2: { months: [7, 8, 9], label: 'JULY – SEPTEMBER' },
  Q3: { months: [10, 11, 12], label: 'OCTOBER – DECEMBER' },
  Q4: { months: [1, 2, 3], label: 'JANUARY – MARCH' },
};

/** month (1–12) → the quarter it falls in. */
export function quarterOfMonth(month: number): Quarter | '' {
  for (const q of QUARTERS) {
    if ((QUARTER_MONTHS[q].months as readonly number[]).includes(month)) return q;
  }
  return '';
}

/**
 * Fold a raw financial-year string onto the canonical `FY27` form, or '' when
 * unrecognised. Accepts: FY27, F27 (the "missing Y" typo), FY 27, FY2027, '27, 2027, 2026/27, 2026-27,
 * 2026/2027, 2026-2027, 2026 - 2027 (the spaced range the retired population
 * carries: '2025 - 2026' → FY26). A split range resolves to its END year; a bare
 * year is read as the FY end year (2027 → FY27). Never throws.
 */
export function normaliseFinancialYear(raw: string | null | undefined): string {
  let s = String(raw ?? '').trim();
  if (!s) return '';
  // Drop a leading FY / F (the "F27" typo — a common data-entry miss for "FY27",
  // Lindiwe 16 Sep 2026) / FY-space and a leading apostrophe. The trailing `y?`
  // makes the Y optional so "F27" folds to FY27 like "FY27" does.
  s = s.replace(/^f\s*y?\s*/i, '').replace(/^'/, '').trim();
  if (!s) return '';
  // Split a range on / or - (with optional surrounding spaces); take the last
  // numeric group. A single token has one group.
  const groups = s.split(/\s*[/-]\s*/).map((g) => g.trim()).filter((g) => /^\d{2}(\d{2})?$/.test(g));
  if (groups.length === 0) return '';
  const last = groups[groups.length - 1];
  const n = Number(last);
  if (!Number.isFinite(n)) return '';
  // 4-digit calendar year → its last two digits are the FY; 2-digit is the FY.
  const fyTwo = last.length === 4 ? n % 100 : n;
  if (fyTwo < 0 || fyTwo > 99) return '';
  // A 4-digit year must be a plausible year (2000–2099) so garbage like '9999'
  // that happens to be 4 digits is rejected.
  if (last.length === 4 && (n < 2000 || n > 2099)) return '';
  return `FY${String(fyTwo).padStart(2, '0')}`;
}

/** Fold a raw period onto Q1..Q4, or '' when unrecognised ("q 1", "Quarter 1", "1"). */
export function normalisePeriod(raw: string | null | undefined): Quarter | '' {
  const n = String(raw ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  const m = n.match(/(?:q|quarter)?([1-4])$/);
  if (m) return `Q${m[1]}` as Quarter;
  return '';
}

/** Extract the {year, month, day} of a date AS OBSERVED in a timezone (SAST default). */
function zonedParts(dateIso: string, tz: string): { year: number; month: number; day: number } | null {
  const d = new Date(dateIso.length === 10 ? `${dateIso}T12:00:00Z` : dateIso);
  if (Number.isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) };
}

/**
 * The {fy, quarter} a calendar date falls in. Apr–Dec → FY = calendarYear + 1;
 * Jan–Mar → FY = calendarYear (SA fiscal year runs Apr–Mar). Returns
 * {fy:'', quarter:''} for an unparseable date.
 */
export function fiscalQuarterOf(
  dateIso: string,
  tz: string = 'Africa/Johannesburg',
): { fy: string; quarter: Quarter | '' } {
  const p = zonedParts(dateIso, tz);
  if (!p) return { fy: '', quarter: '' };
  const quarter = quarterOfMonth(p.month);
  const fyEnd = p.month >= 4 ? p.year + 1 : p.year;
  return { fy: `FY${String(fyEnd % 100).padStart(2, '0')}`, quarter };
}

/** Today's date as yyyy-mm-dd, in SAST (replaces the UTC todayIso). */
export function todayIsoSast(tz: string = 'Africa/Johannesburg'): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** The quarters from Q1 through (and including) `q`, in order. */
export function quartersThrough(q: Quarter): Quarter[] {
  const idx = QUARTERS.indexOf(q);
  return QUARTERS.slice(0, idx + 1);
}

/**
 * The month-range label spanning `fromQ`..`toQ` ("APRIL – SEPTEMBER"). When
 * fromQ === toQ it is that quarter's own label ("APRIL – JUNE").
 */
export function rangeLabel(fromQ: Quarter, toQ: Quarter): string {
  const first = QUARTER_MONTHS[fromQ].label.split(' – ')[0];
  const last = QUARTER_MONTHS[toQ].label.split(' – ')[1] ?? QUARTER_MONTHS[toQ].label;
  return `${first} – ${last}`;
}

/** A cumulative quarter token: "Q1" when through-Q1, else "Q1 – Q3". */
export function quarterSpanLabel(throughQuarter: Quarter): string {
  return throughQuarter === 'Q1' ? 'Q1' : `Q1 – ${throughQuarter}`;
}
